<template>
  <div>
    <PageHeaderBanner>
      <template #title>注文・見積依頼履歴</template>
    </PageHeaderBanner>
    <div class="coh00100-history-table">
      <q-card style="background: var(--q-lightGray)" flat bordered>
        <q-card-section class="q-gutter-y-sm">
          <label>期間</label>
          <div class="row items-center q-ma-none">
            <q-input
              v-model="startDate"
              outlined
              dense
              mask="date"
              class="date q-pb-none q-mr-sm"
              style="width: 140px"
              readonly
            >
              <template #append>
                <q-icon name="event" class="cursor-pointer"></q-icon>
              </template>
              <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                <q-date v-model="startDate" minimal :options="optionsStart">
                  <div class="row items-center justify-end">
                    <q-btn v-close-popup label="OK" color="primary" flat />
                  </div>
                </q-date>
              </q-popup-proxy>
            </q-input>
            <label class="q-mr-sm"> 〜 </label>
            <q-input
              v-model="endDate"
              outlined
              dense
              mask="date"
              class="q-pb-none q-mr-sm"
              style="width: 140px"
              readonly
            >
              <template #append>
                <q-icon name="event" class="cursor-pointer"></q-icon>
              </template>
              <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                <q-date v-model="endDate" minimal :options="optionsEnd">
                  <div class="row items-center justify-end">
                    <q-btn v-close-popup label="OK" color="primary" flat />
                  </div>
                </q-date>
              </q-popup-proxy>
            </q-input>
          </div>
          <div class="row justify-end q-gutter-x-xs">
            <q-btn
              icon="mdi-magnify"
              label="検索"
              color="primary"
              style="width: 150px; padding-left: 0; padding-right: 0"
              @click="search"
            />
            <q-btn
              outline
              style="
                width: 150px;
                background: var(--q-white) !important;
                color: var(--q-delete);
                padding-left: 0;
                padding-right: 0;
              "
              icon="mdi-eraser"
              label="クリア"
              @click="clear"
            />
          </div>
        </q-card-section>
      </q-card>
      <div class="row coh00100-result-table">
        <div
          :class="'chem-table-area nofixed-column ' + (rows.length === 0 ? 'table-nodata' : '')"
          style="width: 100%"
        >
          <q-table
            class="my-sticky-header-column-order-table"
            :rows="rows"
            :columns="columns"
            row-key="index"
            flat
            dense
            separator="cell"
            :hide-no-data="hideNoDataFlg"
            no-data-label="データがありません。"
            :rows-per-page-options="[10, 20, 50, 0]"
            rows-per-page-label="件表示"
            :pagination-label="
              (firstRowIndex, endRowIndex, totalRowsNumber) => {
                return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
              }
            "
          >
            <template #no-data="{ message }">
              <div class="full-width row q-gutter-sm">
                <q-icon size="24px" name="mdi-alert-circle-outline" />
                <span class="q-pt-xs">{{ message }}</span>
              </div>
            </template>
            <template #top>
              <q-btn
                color="primary"
                :class="
                  'export-btn excel-exportbtn ' + (rows.length === 0 ? 'coh00100-disabled-btn' : '')
                "
                :outline="data.length !== 0 ? true : false"
                icon="mdi-download"
                no-caps
                :disable="rows.length === 0"
                @click="displayOutExcelDialog"
                >Excel出力</q-btn
              >
              <q-tooltip
                v-if="rows.length === 0"
                anchor="top right"
                :offset="[-70, 8]"
                self="bottom middle"
              >
                <strong>検索結果が存在しません。</strong>
              </q-tooltip>
            </template>
          </q-table>
        </div>
      </div>
    </div>
    <!-- Excel出力確認ダイアログ -->
    <AlertDialog
      :display="isOutExcelDialog"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isOutExcelDialog = false"
      @confirm="download"
      @close="isOutExcelDialog = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Loading } from 'quasar';
import { useRouter } from 'vue-router';
import saveAs from 'file-saver';
import http from '../common/axios-common';
import HttpResponseStatusCode from '../common/code/HttpResponseStatusCode';
import base64toBlob from '../common/utils/fileUtils';
import PageHeaderBanner from '../components/banner/PageHeaderBanner.vue';
import AlertDialog from '../components/dialogs/AlertDialog.vue';

// 検索結果
interface Result {
  requestTimestamp: string;
  requestId: string;
  requestType: string;
  requestCustomerCode: string | null;
  requestCustomerName: string | null;
  requestUserMailAddress: string;
  requestUserName: string;
  requestCount: number;
}
const router = useRouter();

// 取得したデータ
const data = ref<Array<Result>>([]);

// 表示するデータ
const rows = ref<Array<Result>>(data.value);

// 検索条件：from
const startDate = ref('');

// 検索条件：to
const endDate = ref('');

// Dialogタイトル
const dialogTitle = ref('');

// Dialog表示レベル
const dialogLevel = ref('');

// Dialog本文
const dialogText = ref(['']);

// 確認Dialogフラグ
const isConfFlg = ref(false);

// Excel出力確認Dialog表示フラグ
const isOutExcelDialog = ref(false);

// 初期表示読み込み時「データがありません」を非表示にするためのフラグ(falseで表示)
const hideNoDataFlg = ref(true);

// Excelファイル出力の検索条件:from
const from = ref('');

// Excelファイル出力の検索条件:to
const to = ref('');

// クリアボタン処理
const clear = () => {
  startDate.value = '';
  endDate.value = '';
};

// カラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: Array<any> = [
  {
    name: 'requestTimestamp',
    label: '依頼日時',
    align: 'left',
    field: 'requestTimestamp',
    sortable: true,
    style: 'width: 120px',
  },
  {
    name: 'requestId',
    label: '依頼No.',
    align: 'left',
    field: 'requestId',
    sortable: true,
  },
  {
    name: 'requestType',
    label: '依頼区分',
    align: 'left',
    field: 'requestType',
    sortable: true,
    style: 'width: 40px',
  },
  {
    name: 'requestCustomerCode',
    label: '得意先コード',
    align: 'left',
    field: 'requestCustomerCode',
    sortable: true,
    style: 'width: 110px',
  },
  {
    name: 'requestCustomerName',
    label: '得意先名｜納品場所',
    align: 'left',
    field: 'requestCustomerName',
    sortable: true,
  },
  {
    name: 'requestMailAddress',
    label: '会員ID（メールアドレス）',
    align: 'left',
    field: 'requestMailAddress',
    sortable: true,
  },
  {
    name: 'requestUserName',
    label: '会員名',
    align: 'left',
    field: 'requestUserName',
    sortable: true,
  },
  {
    name: 'requestCount',
    label: '件数',
    align: 'left',
    classes: 'text-right',
    field: 'requestCount',
    sortable: true,
  },
];

/**
 * Excel出力確認ダイアログ表示
 */
const displayOutExcelDialog = () => {
  isOutExcelDialog.value = true;
  dialogTitle.value = 'Excel出力確認';
  dialogLevel.value = 'information';
  dialogText.value = ['注文・見積依頼履歴のExcelファイルを出力します。', 'よろしいですか？'];
  isConfFlg.value = false;
};

// startDateの値とendDateの値が互いの値により制御されるメソッド
const optionsStart = (stDate: string) => {
  if (endDate.value) {
    return stDate <= endDate.value && stDate >= '1999/01/01';
  }
  return stDate <= '2999/12/31' && stDate >= '1999/01/01';
};
const optionsEnd = (edDate: string) => edDate >= startDate.value && edDate <= '2999/12/31';

// 注文・見積依頼履歴取得
const getRequestHistory = () => {
  // Loading Start
  Loading.show();
  http
    .post('/coh001001/requestHistory')
    .then((response) => {
      data.value = response.data.historyList;
      rows.value = data.value;
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        //  エラー画面に遷移
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
      hideNoDataFlg.value = false;
    });
};

// 注文・見積依頼履歴日付絞り込み検索
const search = () => {
  // Loading Start
  Loading.show();
  from.value = startDate.value;
  to.value = endDate.value;
  const formData = new FormData();
  formData.append('startDate', startDate.value);
  formData.append('endDate', endDate.value);
  http
    .post('/coh001001/requestHistory', formData)
    .then((response) => {
      data.value = response.data.historyList;
      rows.value = data.value;
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他のエラーの場合
        //  エラー画面に遷移
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

// Excel出力
const download = () => {
  // Loading Start
  Loading.show();

  const formData = new FormData();
  formData.append('startDate', from.value);
  formData.append('endDate', to.value);
  http
    .post('/coh001001/downloadHistory', formData)
    .then((response) => {
      const blob = new Blob(
        [
          base64toBlob(
            response.data.body,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          ),
        ],
        {}
      );
      saveAs(blob, response.data.file_name);
      // ダイアログ表示
      dialogTitle.value = 'Excel出力完了';
      dialogLevel.value = 'information';
      dialogText.value = ['注文・見積依頼履歴のExcelファイルを出力しました。'];
      isConfFlg.value = true;
      isOutExcelDialog.value = true;
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他のエラーの場合
        //  エラー画面に遷移
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

// 初期表示
getRequestHistory();
</script>

<style scoped>
.coh00100-history-table {
  width: 80%;
  margin: 0 auto;
}
.coh00100-result-table {
  margin-top: 20px;
}
</style>
<style>
.coh00100-result-table .chem-table-area .q-table__container {
  margin-top: 50px;
}
.coh00100-history-table .q-field--outlined.q-field--readonly .q-field__control:before {
  border-style: solid;
}

.coh00100-history-table label.q-field {
  background-color: var(--q-pureWhite);
}

.coh00100-result-table .chem-table-area .q-table__bottom.row {
  right: 140px;
}

.coh00100-result-table .coh00100-disabled-btn {
  background-color: var(--q-opacityDisable) !important;
  color: #fff !important;
}
.coh00100-result-table .chem-table-area .q-table__top.relative-position.row {
  left: unset;
  right: 0;
  border: none;
}

.my-sticky-header-column-order-table {
  max-height: 385px;
}

.my-sticky-header-column-order-table thead tr th {
  position: sticky;
  z-index: 1;
}

.my-sticky-header-column-order-table thead tr:first-child th {
  top: 0;
}
</style>
